'use client'

/**
 * Pont entre la mini-fenêtre de recherche détachée (/search-window) et la
 * fenêtre principale BLC Music. Doit être monté dans le layout `/app` pour
 * que la grande fenêtre écoute en permanence.
 *
 * Mécanisme :
 *  - La mini-fenêtre poste sur BroadcastChannel('blc-music') des messages
 *    { type: 'play', track, queue }.
 *  - Ce composant écoute, et appelle ctx.play(track, queue) sur le
 *    PlayerContext de la grande fenêtre.
 *
 * Bonus Electron : on accepte aussi `window.blcDesktop.onMiniSearchPlay`
 * (callback IPC venant du main process via deep-link blc-music://play?q=…).
 */

import { useEffect } from 'react'
import { usePlayer } from '@/lib/player-context'

export default function MiniSearchBridge() {
  const { play } = usePlayer()

  useEffect(() => {
    if (typeof window === 'undefined' || !('BroadcastChannel' in window)) return
    const channel = new BroadcastChannel('blc-music')

    const onMessage = (e: MessageEvent) => {
      const msg = e.data
      if (!msg || msg.type !== 'play' || !msg.track) return
      try {
        play(msg.track, Array.isArray(msg.queue) ? msg.queue : [msg.track])
      } catch (err) {
        console.warn('[BLC mini-search] play failed', err)
      }
    }
    channel.addEventListener('message', onMessage)
    return () => {
      channel.removeEventListener('message', onMessage)
      channel.close()
    }
  }, [play])

  // Bridge Electron : si l'app desktop signale un play depuis la mini-search
  // (ou depuis un deep-link blc-music://search?q=…)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const desktop = (window as any).blcDesktop
    if (!desktop?.onMiniSearchPlay) return
    const off = desktop.onMiniSearchPlay((payload: any) => {
      if (payload?.track) {
        try {
          play(payload.track, Array.isArray(payload.queue) ? payload.queue : [payload.track])
        } catch {}
      }
    })
    return () => { off?.() }
  }, [play])

  return null
}

/**
 * Hook pour ouvrir la mini-fenêtre de recherche depuis n'importe quel composant.
 *
 * Stratégie :
 *  1. Si on est en Electron avec blcDesktop.openSearchWindow → ouvre une vraie
 *     BrowserWindow frameless (à venir côté main.js)
 *  2. Sinon → window.open() avec dimensions limitées (mini popup browser)
 *
 * Accepte options { q?, autoplay? } pour pré-remplir / auto-jouer.
 */
export function useMiniSearch() {
  return {
    open: (opts?: { q?: string; autoplay?: boolean; close?: boolean }) => {
      if (typeof window === 'undefined') return
      const params = new URLSearchParams()
      if (opts?.q) params.set('q', opts.q)
      if (opts?.autoplay) params.set('autoplay', '1')
      if (opts?.close) params.set('close', '1')
      const url = `/search-window${params.toString() ? '?' + params.toString() : ''}`

      // Mode Electron desktop — fenêtre native sans bandeau Chrome
      const desktop = (window as any).blcDesktop
      if (desktop?.openSearchWindow) {
        try { desktop.openSearchWindow(opts || {}) ; return } catch {}
      }

      // Fallback navigateur — popup standard
      const features = [
        'width=420',
        'height=540',
        'menubar=no',
        'toolbar=no',
        'location=no',
        'status=no',
        'resizable=yes',
      ].join(',')
      window.open(url, 'blc-mini-search', features)
    },
  }
}
